// Mantine
import { Button, Modal, Select } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { DatePickerInput } from '@mantine/dates'

// UI
import LocationSelect from '../ui/LocationSelect'

// React
import { useState, useEffect } from 'react'

// Dayjs
import dayjs from 'dayjs'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

export default function FulfillmentOrderEdit({
    fulfillment_order,
    loadComponent,
}) {
    const [opened, handlers] = useDisclosure(false)

    // Fields
    const [location, setLocation] = useState(
        fulfillment_order.location_id.toString()
    )

    const [date, setDate] = useState(
        fulfillment_order.fulfill_at
            ? dayjs(fulfillment_order.fulfill_at).toDate()
            : null
    )

    const [warrantiesMode, setWarrantiesMode] = useState(
        fulfillment_order.warranties_mode
    )

    const [fulfillmentMode, setFulfillmentMode] = useState(
        fulfillment_order.fulfillment_mode
    )

    const [incoterm, setIncoterm] = useState(
        fulfillment_order.incoterm ?? 'DAP'
    )

    // Loading
    const [isLoading, setIsLoading] = useState(false)

    // Reset the form when the modal is opened
    useEffect(() => {
        if (opened) {
            setLocation(fulfillment_order.location_id.toString())

            setDate(
                fulfillment_order.fulfill_at
                    ? dayjs(fulfillment_order.fulfill_at).toDate()
                    : null
            )

            setWarrantiesMode(fulfillment_order.warranties_mode)
            setIncoterm(fulfillment_order.incoterm ?? 'DAP')

            setIsLoading(false)
        }
    }, [opened])

    // Form submit
    const { requestPatch } = useAxiosApi()

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsLoading(true)

        await requestPatch(`/fulfillment-orders/${fulfillment_order.id}`, {
            location_id: location,
            fulfill_at: dayjs(date).format('YYYY-MM-DD'),
            warranties_mode: warrantiesMode,
            fulfillment_mode: fulfillmentMode,
            incoterm,
        })

        await loadComponent()

        handlers.close()

        setIsLoading(false)
    }

    return (
        <>
            <Button variant="default" onClick={handlers.open}>
                Modifier
            </Button>

            <Modal
                title="Modifier la demande de préparation"
                opened={opened}
                onClose={handlers.close}
            >
                <div className="flex flex-col gap-2">
                    {fulfillment_order.shopify_id === null && (
                        <LocationSelect
                            label="Lieu de préparation"
                            value={location}
                            onChange={setLocation}
                        />
                    )}

                    <DatePickerInput
                        label="Date de préparation"
                        value={date}
                        onChange={setDate}
                        locale="fr"
                        valueFormat="LL"
                        minDate={dayjs().toDate()}
                    />

                    <Select
                        label="Cartes de garanties"
                        allowDeselect={false}
                        value={warrantiesMode}
                        onChange={setWarrantiesMode}
                        data={[
                            {
                                value: 'associate',
                                label: 'Associer',
                            },
                            {
                                value: 'blank',
                                label: 'Cartes vierges',
                            },
                            {
                                value: 'none',
                                label: 'Pas de cartes',
                            },
                        ]}
                    />

                    <Select
                        label="Déclaration douanière"
                        description="En cas de passage de douane, définit qui paie les droits"
                        allowDeselect={false}
                        value={incoterm}
                        onChange={setIncoterm}
                        data={[
                            {
                                value: 'DAP',
                                label: 'Le destinataire paie les droits de douane (DAP)',
                            },
                            {
                                value: 'DDP',
                                label: 'Droits de douane prépayés (DDP)',
                            },
                        ]}
                    />

                    <Select
                        label="Mode de préparation"
                        allowDeselect={false}
                        value={fulfillmentMode}
                        onChange={setFulfillmentMode}
                        data={[
                            {
                                value: 'shipment',
                                label: 'Expédition par transporteur',
                            },
                            {
                                value: 'pickup',
                                label: 'Remise en main propre',
                            },
                        ]}
                    />

                    <div className="grid grid-cols-2 gap-2 mt-5">
                        <Button onClick={handlers.close} variant="default">
                            Annuler
                        </Button>
                        <Button onClick={handleSubmit} loading={isLoading}>
                            Modifier
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    )
}
